import type { Application } from 'pixi.js';
import type { CommandBus } from '../bridge/CommandBus';
import { pickTile, type PickedTile } from '../render/iso';
import { HALF_H, HALF_W } from '../render/iso';
import {
  BUILDING_DEFS,
  CABLE_COST_CENTS,
  MAX_DEPTH,
  PAVED_COST_CENTS,
  PIPE_COST_CENTS,
  RECLAIM_COST_CENTS_PER_LEVEL,
  ROAD_COST_CENTS,
  ROUTE_SALVAGE_FRACTION,
  STAKE,
  colOf,
  inBounds,
  xOf,
  yOf,
} from '../sim/constants';
import type { Simulation } from '../sim/sim';
import { FLAG_BUILDING, FLAG_CABLE, FLAG_PAVED, FLAG_PIPE, FLAG_ROAD, type BuildingType, type Speed } from '../sim/types';
import { hasFlag, isDiggable } from '../sim/world/terrain';
import { findRoutePath, type RouteKind } from '../sim/world/path';
import { CameraController } from './CameraController';

export type Tool = 'idle' | 'dig' | 'cancelDig' | 'road' | 'pipe' | 'cable' | 'paved' | 'reclaim' | 'demolish' | 'place';

/**
 * All canvas input: camera pan/zoom, tile hover picking with level lock, and
 * the dig/cancel/build/route/demolish tools. All mutations go via CommandBus.
 */
export class InputController {
  readonly camera = new CameraController();
  hover: PickedTile | null = null;
  lockedLevel: number | null = null;
  tool: Tool = 'idle';
  placeType: BuildingType | null = null;
  selectedBuildingId: number | null = null;
  /** Tiles in the in-progress drag (dig rect or road/pipe stroke). */
  readonly pendingCols = new Set<number>();
  /** Bumped on every pendingCols mutation — cheap dirty key for overlay/HUD. */
  pendingVersion = 0;
  /** True while an A*-routed drag has no legal path to the hover tile. */
  routeBlocked = false;

  private readonly app: Application;
  private readonly sim: Simulation;
  private readonly bus: CommandBus;
  private readonly keys = new Set<string>();
  private lastSpeed: Speed = 1;
  private panPointer: number | null = null;
  private lastPanX = 0;
  private lastPanY = 0;
  private panTravel = 0;
  private dragAnchor: PickedTile | null = null;
  private dragButton = 0;
  private cursorX = 0;
  private cursorY = 0;
  /** Shift (paint) or right-button (erase) latched a freeform stroke. */
  private freeformStroke = false;
  /** Last col fed to the stroke — gaps to the next sample are interpolated. */
  private lastStrokeCol = -1;
  private routePreviewTarget = -1;
  private routePreviewTerrainVersion = -1;
  private downClientX = 0;
  private downClientY = 0;
  onToolChange: (() => void) | null = null;

  constructor(app: Application, sim: Simulation, bus: CommandBus) {
    this.app = app;
    this.sim = sim;
    this.bus = bus;
    const canvas = app.canvas;
    canvas.style.touchAction = 'none';
    canvas.addEventListener('contextmenu', (event) => event.preventDefault());
    canvas.addEventListener('pointerdown', (event) => this.onPointerDown(event));
    canvas.addEventListener('pointermove', (event) => this.onPointerMove(event));
    canvas.addEventListener('pointerup', (event) => this.onPointerUp(event));
    canvas.addEventListener('pointercancel', (event) => this.onPointerUp(event));
    canvas.addEventListener('wheel', (event) => this.onWheel(event), { passive: false });
    window.addEventListener('keydown', (event) => this.onKeyDown(event));
    window.addEventListener('keyup', (event) => this.keys.delete(event.code));
    window.addEventListener('blur', () => this.keys.clear());
    this.camera.centerOn((STAKE.x - STAKE.y) * HALF_W, (STAKE.x + STAKE.y) * HALF_H);
  }

  private get screenW(): number {
    return this.app.renderer.width / this.app.renderer.resolution;
  }

  private get screenH(): number {
    return this.app.renderer.height / this.app.renderer.resolution;
  }

  setTool(tool: Tool, placeType: BuildingType | null = null): void {
    this.tool = tool;
    this.placeType = tool === 'place' ? placeType : null;
    this.dragAnchor = null;
    this.clearPending();
    this.onToolChange?.();
  }

  private clearPending(): void {
    this.pendingCols.clear();
    this.pendingVersion += 1;
    this.routeBlocked = false;
    this.freeformStroke = false;
    this.lastStrokeCol = -1;
    this.routePreviewTarget = -1;
  }

  private isRouteTool(): boolean {
    return this.tool === 'road' || this.tool === 'pipe' || this.tool === 'cable' || this.tool === 'paved';
  }

  /** True while the active drag removes rather than builds. */
  get erasing(): boolean {
    return this.dragAnchor !== null && (this.dragButton === 2 || this.tool === 'cancelDig');
  }

  private onPointerDown(event: PointerEvent): void {
    this.updateCursor(event);
    this.downClientX = event.clientX;
    this.downClientY = event.clientY;
    const middle = event.button === 1;
    if (middle || (event.button === 0 && this.tool === 'idle')) {
      this.panPointer = event.pointerId;
      this.lastPanX = event.clientX;
      this.lastPanY = event.clientY;
      this.panTravel = 0;
      this.app.canvas.setPointerCapture(event.pointerId);
      return;
    }
    if (event.button === 2 && !(this.tool === 'dig' || this.isRouteTool())) {
      // Right-click exits the active tool (RCT convention). Dig and route
      // tools own right-drag for erasing; they exit on a stationary
      // right-click at pointer-up instead.
      this.setTool('idle');
      return;
    }
    const picked = this.pick();
    if (!picked) return;
    switch (this.tool) {
      case 'dig':
      case 'cancelDig':
        this.dragAnchor = picked;
        this.dragButton = event.button;
        this.updateDigRect(picked);
        break;
      case 'reclaim':
        this.dragAnchor = picked;
        this.dragButton = event.button;
        this.updateReclaimRect(picked);
        break;
      case 'road':
      case 'pipe':
      case 'cable':
      case 'paved': {
        this.dragAnchor = picked;
        this.dragButton = event.button;
        this.routeBlocked = false;
        this.routePreviewTarget = -1;
        // Shift latches freeform paint for the whole stroke; the right
        // button always erases freeform.
        this.freeformStroke = event.shiftKey || event.button === 2;
        const col = colOf(picked.x, picked.y);
        this.lastStrokeCol = col;
        if (this.freeformStroke) this.addRouteTile(col, event.button === 2);
        else this.rebuildRoutePreview(col);
        break;
      }
      case 'place':
        if (this.placeType) {
          this.bus.dispatch({ kind: 'placeBuilding', type: this.placeType, x: picked.x, y: picked.y });
        }
        break;
      case 'demolish': {
        const building = this.buildingAt(picked.x, picked.y);
        if (building) this.bus.dispatch({ kind: 'demolish', buildingId: building.id });
        break;
      }
      default:
        break;
    }
  }

  private onPointerMove(event: PointerEvent): void {
    this.updateCursor(event);
    if (this.panPointer === event.pointerId) {
      this.panTravel += Math.abs(event.clientX - this.lastPanX) + Math.abs(event.clientY - this.lastPanY);
      this.camera.panByScreen(event.clientX - this.lastPanX, event.clientY - this.lastPanY);
      this.lastPanX = event.clientX;
      this.lastPanY = event.clientY;
      return;
    }
    this.hover = this.pick();
    if (this.dragAnchor) {
      if (this.tool === 'dig' || this.tool === 'cancelDig') {
        const current = this.pickAtLevel(this.dragAnchor.z);
        if (current) this.updateDigRect(current);
      } else if (this.tool === 'reclaim') {
        const current = this.pickAtLevel(this.dragAnchor.z);
        if (current) this.updateReclaimRect(current);
      } else if (this.isRouteTool() && this.hover) {
        const col = colOf(this.hover.x, this.hover.y);
        if (this.freeformStroke) this.strokeTo(col, this.dragButton === 2);
        else this.rebuildRoutePreview(col);
      }
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.panPointer === event.pointerId) {
      this.panPointer = null;
      if (this.panTravel < 5) {
        // A stationary click in idle mode selects the building under it.
        const picked = this.pick();
        const building = picked ? this.buildingAt(picked.x, picked.y) : null;
        this.selectedBuildingId = building ? building.id : null;
      }
      return;
    }
    if (this.dragAnchor) {
      const travel =
        Math.abs(event.clientX - this.downClientX) + Math.abs(event.clientY - this.downClientY);
      const isClick = travel < 5;
      const anchorCol = colOf(this.dragAnchor.x, this.dragAnchor.y);
      const cols = [...this.pendingCols];
      if (this.tool === 'dig' || this.tool === 'cancelDig') {
        const removing = this.tool === 'cancelDig' || this.dragButton === 2;
        if (!removing && isClick && this.sim.state.orders.has(anchorCol)) {
          // A stationary click on an existing mark cancels it. (Deepening
          // comes from designating the dug floor again after completion.)
          this.bus.dispatch({ kind: 'cancelDig', cols: [anchorCol] });
        } else if (cols.length > 0) {
          this.bus.dispatch(removing ? { kind: 'cancelDig', cols } : { kind: 'designateDig', cols });
        }
      } else if (this.isRouteTool()) {
        if (this.dragButton === 2) {
          if (cols.length > 0) {
            const kind =
              this.tool === 'road'
                ? 'removeRoad'
                : this.tool === 'pipe'
                  ? 'removePipe'
                  : this.tool === 'cable'
                    ? 'removeCable'
                    : 'removePaved';
            this.bus.dispatch({ kind, cols });
          } else if (isClick) {
            // Right-click on nothing erasable still exits the tool.
            this.dragAnchor = null;
            this.clearPending();
            this.setTool('idle');
            return;
          }
        } else if (!this.routeBlocked && cols.length > 0) {
          if (this.tool === 'road') this.bus.dispatch({ kind: 'paintRoad', cols });
          else if (this.tool === 'pipe') this.bus.dispatch({ kind: 'paintPipe', cols });
          else if (this.tool === 'cable') this.bus.dispatch({ kind: 'paintCable', cols });
          else this.bus.dispatch({ kind: 'paintPaved', cols });
        }
      } else if (this.tool === 'reclaim' && cols.length > 0) {
        this.bus.dispatch({ kind: 'paintReclaim', cols });
      }
      this.dragAnchor = null;
      this.clearPending();
    }
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault();
    const rect = this.app.canvas.getBoundingClientRect();
    const sx = event.clientX - rect.left;
    const sy = event.clientY - rect.top;
    this.camera.zoomStep(event.deltaY < 0 ? 1 : -1, sx, sy, this.screenW, this.screenH);
  }

  private onKeyDown(event: KeyboardEvent): void {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;
    // A modal, the Gazette, or the title screen owns the screen — don't let
    // hotkeys unpause or switch tools underneath it.
    if (document.querySelector('.modal-backdrop, .gazette-backdrop, .title-backdrop')) return;
    this.keys.add(event.code);
    switch (event.code) {
      case 'Space': {
        event.preventDefault();
        const speed = this.sim.state.meta.speed;
        if (speed === 0) {
          this.bus.dispatch({ kind: 'setSpeed', speed: this.lastSpeed });
        } else {
          this.lastSpeed = speed;
          this.bus.dispatch({ kind: 'setSpeed', speed: 0 });
        }
        break;
      }
      case 'Digit1':
        this.bus.dispatch({ kind: 'setSpeed', speed: 1 });
        break;
      case 'Digit2':
        this.bus.dispatch({ kind: 'setSpeed', speed: 3 });
        break;
      case 'Digit3':
        this.bus.dispatch({ kind: 'setSpeed', speed: 8 });
        break;
      case 'KeyE':
        this.setTool(this.tool === 'dig' ? 'idle' : 'dig');
        break;
      case 'KeyX':
        this.setTool(this.tool === 'cancelDig' ? 'idle' : 'cancelDig');
        break;
      case 'KeyR':
        this.setTool(this.tool === 'road' ? 'idle' : 'road');
        break;
      case 'KeyU':
        this.setTool(this.tool === 'pipe' ? 'idle' : 'pipe');
        break;
      case 'KeyC':
        this.setTool(this.tool === 'cable' ? 'idle' : 'cable');
        break;
      // Q, not Escape: browsers reserve Esc for exiting fullscreen.
      case 'KeyQ':
        this.setTool('idle');
        break;
      case 'BracketLeft':
        this.lockedLevel = this.lockedLevel === null ? 0 : Math.min(MAX_DEPTH, this.lockedLevel + 1);
        this.onToolChange?.();
        break;
      case 'BracketRight':
        if (this.lockedLevel !== null) {
          this.lockedLevel = this.lockedLevel === 0 ? null : this.lockedLevel - 1;
          this.onToolChange?.();
        }
        break;
      case 'Digit0':
        this.lockedLevel = null;
        this.onToolChange?.();
        break;
      case 'Home':
        this.camera.centerOn((STAKE.x - STAKE.y) * HALF_W, (STAKE.x + STAKE.y) * HALF_H);
        break;
      default:
        break;
    }
  }

  private updateCursor(event: PointerEvent): void {
    const rect = this.app.canvas.getBoundingClientRect();
    this.cursorX = event.clientX - rect.left;
    this.cursorY = event.clientY - rect.top;
  }

  private pick(): PickedTile | null {
    const world = this.camera.screenToWorld(this.cursorX, this.cursorY, this.screenW, this.screenH);
    return pickTile(world.x, world.y, this.sim.state.terrain, this.lockedLevel);
  }

  buildingAt(x: number, y: number): { id: number } | null {
    return (
      this.sim.state.buildings.find((candidate) => {
        const def = BUILDING_DEFS[candidate.type];
        return x >= candidate.x && x < candidate.x + def.w && y >= candidate.y && y < candidate.y + def.h;
      }) ?? null
    );
  }

  private pickAtLevel(level: number): PickedTile | null {
    const world = this.camera.screenToWorld(this.cursorX, this.cursorY, this.screenW, this.screenH);
    return pickTile(world.x, world.y, this.sim.state.terrain, level);
  }

  /** Rectangle between anchor and current, restricted to the anchor's level. */
  private updateDigRect(current: PickedTile): void {
    const anchor = this.dragAnchor;
    if (!anchor) return;
    this.pendingCols.clear();
    const minX = Math.min(anchor.x, current.x);
    const maxX = Math.max(anchor.x, current.x);
    const minY = Math.min(anchor.y, current.y);
    const maxY = Math.max(anchor.y, current.y);
    const removing = this.tool === 'cancelDig' || this.dragButton === 2;
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        if (!inBounds(x, y)) continue;
        const col = colOf(x, y);
        const depth = this.sim.state.terrain.depth[col] ?? 0;
        if (depth !== anchor.z) continue;
        if (removing) {
          if (this.sim.state.orders.has(col)) this.pendingCols.add(col);
        } else if (isDiggable(this.sim.state.terrain, col) && !this.sim.state.orders.has(col)) {
          // Already-marked tiles stay out of a designate rect — re-marking
          // is a no-op in the sim, and a click on one cancels instead.
          this.pendingCols.add(col);
        }
      }
    }
    this.pendingVersion += 1;
  }

  /** Rectangle between anchor and current: every excavated column that could
   * legally be filled (mirrors the sim's paintReclaim checks). */
  private updateReclaimRect(current: PickedTile): void {
    const anchor = this.dragAnchor;
    if (!anchor) return;
    this.pendingCols.clear();
    const terrain = this.sim.state.terrain;
    const minX = Math.min(anchor.x, current.x);
    const maxX = Math.max(anchor.x, current.x);
    const minY = Math.min(anchor.y, current.y);
    const maxY = Math.max(anchor.y, current.y);
    for (let y = minY; y <= maxY; y += 1) {
      for (let x = minX; x <= maxX; x += 1) {
        if (!inBounds(x, y)) continue;
        const col = colOf(x, y);
        if ((terrain.depth[col] ?? 0) === 0) continue;
        if (hasFlag(terrain, col, FLAG_BUILDING)) continue;
        if (this.sim.state.piles.has(col) || this.sim.state.goodsPiles.has(col)) continue;
        this.pendingCols.add(col);
      }
    }
    this.pendingVersion += 1;
  }

  /** The route tool's own flag — what it builds, and what erase targets. */
  private routeFlag(): number {
    return this.tool === 'road'
      ? FLAG_ROAD
      : this.tool === 'pipe'
        ? FLAG_PIPE
        : this.tool === 'cable'
          ? FLAG_CABLE
          : FLAG_PAVED;
  }

  private addRouteTile(col: number, erase: boolean): void {
    const terrain = this.sim.state.terrain;
    if (erase) {
      // Erase strokes collect tiles that carry the tool's flag.
      if (!hasFlag(terrain, col, this.routeFlag())) return;
    } else {
      // Roads can't run through buildings; pipes and cables run UNDER them.
      if (this.tool === 'road' && (hasFlag(terrain, col, FLAG_BUILDING) || hasFlag(terrain, col, FLAG_ROAD))) return;
      if (this.tool === 'pipe' && hasFlag(terrain, col, FLAG_PIPE)) return;
      if (this.tool === 'cable' && hasFlag(terrain, col, FLAG_CABLE)) return;
      if (this.tool === 'paved' && (!hasFlag(terrain, col, FLAG_ROAD) || hasFlag(terrain, col, FLAG_PAVED))) return;
    }
    this.pendingCols.add(col);
    this.pendingVersion += 1;
  }

  /** Freeform stroke: walk a 4-connected line from the last sample so a fast
   * drag can't leave gaps that would silently break a flood-fill network. */
  private strokeTo(targetCol: number, erase: boolean): void {
    if (this.lastStrokeCol < 0) this.lastStrokeCol = targetCol;
    if (targetCol === this.lastStrokeCol) return;
    const x1 = xOf(targetCol);
    const y1 = yOf(targetCol);
    let x = xOf(this.lastStrokeCol);
    let y = yOf(this.lastStrokeCol);
    const dx = Math.sign(x1 - x);
    const dy = Math.sign(y1 - y);
    const adx = Math.abs(x1 - x);
    const ady = Math.abs(y1 - y);
    while (x !== x1 || y !== y1) {
      const remX = Math.abs(x1 - x);
      const remY = Math.abs(y1 - y);
      // Step along whichever axis has the larger remaining fraction, so the
      // interpolation hugs the ideal line while staying 4-connected.
      if (remY === 0 || (remX !== 0 && remX * ady >= remY * adx)) x += dx;
      else y += dy;
      this.addRouteTile(colOf(x, y), erase);
    }
    this.lastStrokeCol = targetCol;
  }

  /** Routed drag: preview the A* path from the anchor to the hover tile. */
  private rebuildRoutePreview(targetCol: number): void {
    const anchor = this.dragAnchor;
    if (!anchor) return;
    const terrain = this.sim.state.terrain;
    if (targetCol === this.routePreviewTarget && terrain.version === this.routePreviewTerrainVersion) return;
    this.routePreviewTarget = targetCol;
    this.routePreviewTerrainVersion = terrain.version;
    this.pendingCols.clear();
    this.routeBlocked = false;
    const anchorCol = colOf(anchor.x, anchor.y);
    // Anchor legality mirrors findRoutePath's passability.
    const anchorOk =
      this.tool === 'road'
        ? !hasFlag(terrain, anchorCol, FLAG_BUILDING)
        : this.tool === 'paved'
          ? hasFlag(terrain, anchorCol, FLAG_ROAD)
          : true;
    if (!anchorOk) {
      this.routeBlocked = true;
      this.pendingCols.add(anchorCol);
      this.pendingVersion += 1;
      return;
    }
    const path =
      targetCol === anchorCol ? [] : findRoutePath(terrain, anchorCol, targetCol, this.tool as RouteKind);
    if (path === null) {
      this.routeBlocked = true;
      this.pendingCols.add(anchorCol);
      this.pendingVersion += 1;
      return;
    }
    // The full route rides in the preview, existing tiles included — the sim
    // skips them and the cost helpers don't count them.
    this.pendingCols.add(anchorCol);
    for (const col of path) this.pendingCols.add(col);
    this.pendingVersion += 1;
  }

  /** Cents the current pending paint would cost (existing tiles are free). */
  pendingCostCents(): number {
    if (this.erasing) return 0;
    const terrain = this.sim.state.terrain;
    switch (this.tool) {
      case 'road':
      case 'pipe':
      case 'cable':
      case 'paved':
        return this.pendingNewTileCount() * this.routeCostPerTile();
      case 'reclaim': {
        let total = 0;
        for (const col of this.pendingCols) total += (terrain.depth[col] ?? 0) * RECLAIM_COST_CENTS_PER_LEVEL;
        return total;
      }
      default:
        return 0;
    }
  }

  /** Pending route tiles that don't already carry the tool's flag. */
  pendingNewTileCount(): number {
    if (!this.isRouteTool()) return this.pendingCols.size;
    const terrain = this.sim.state.terrain;
    const flag = this.routeFlag();
    let count = 0;
    for (const col of this.pendingCols) if (!hasFlag(terrain, col, flag)) count += 1;
    return count;
  }

  /** Cents the current pending erase would refund. */
  pendingRefundCents(): number {
    if (!this.erasing || !this.isRouteTool()) return 0;
    const terrain = this.sim.state.terrain;
    const perTile = Math.floor(this.routeCostPerTile() * ROUTE_SALVAGE_FRACTION);
    let total = 0;
    for (const col of this.pendingCols) {
      total += perTile;
      if (this.tool === 'road' && hasFlag(terrain, col, FLAG_PAVED)) {
        total += Math.floor(PAVED_COST_CENTS * ROUTE_SALVAGE_FRACTION);
      }
    }
    return total;
  }

  private routeCostPerTile(): number {
    return this.tool === 'road'
      ? ROAD_COST_CENTS
      : this.tool === 'pipe'
        ? PIPE_COST_CENTS
        : this.tool === 'cable'
          ? CABLE_COST_CENTS
          : PAVED_COST_CENTS;
  }

  /** Per-frame: held-key panning. */
  update(dtSeconds: number): void {
    const left = this.keys.has('KeyA') || this.keys.has('ArrowLeft');
    const right = this.keys.has('KeyD') || this.keys.has('ArrowRight');
    const up = this.keys.has('KeyW') || this.keys.has('ArrowUp');
    const down = this.keys.has('KeyS') || this.keys.has('ArrowDown');
    if (left || right || up || down) {
      this.camera.panKeys(dtSeconds, left, right, up, down);
    }
  }
}
